"use client";

import mapboxgl from "mapbox-gl";
import type React from "react";
import { useCallback, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useMap } from "../context";

interface PopupProps extends mapboxgl.PopupOptions {
  children: React.ReactNode;
  latitude?: number;
  longitude?: number;
  marker?: mapboxgl.Marker;
  onClose?: () => void;
}

export function Popup({
  latitude,
  longitude,
  children,
  marker,
  onClose,
  className,
  ...props
}: PopupProps) {
  const { map } = useMap();

  const container = useMemo(() => document.createElement("div"), []);

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: props spread changes reference every render
  useEffect(() => {
    if (!map) {
      return;
    }

    const popup = new mapboxgl.Popup({
      ...props,
      className: `mapboxgl-custom-popup ${className ?? ""}`,
    })
      .setDOMContent(container)
      .setMaxWidth("none");

    popup.on("close", handleClose);

    if (marker) {
      marker.getPopup()?.remove();
      marker.setPopup(popup);
      marker.togglePopup();
    } else if (latitude !== undefined && longitude !== undefined) {
      popup.setLngLat([longitude, latitude]).addTo(map);
    }

    return () => {
      popup.off("close", handleClose);
      popup.remove();
      if (marker?.getPopup()) {
        marker.setPopup(null as unknown as mapboxgl.Popup);
      }
    };
  }, [map, marker, latitude, longitude, className, container, handleClose]);

  return createPortal(children, container);
}
